import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import { GROUPS, toBool, Toggle, Check } from './RoleTableLegacy';
import type { Role } from './RoleTableLegacy';
import InlineTable, { InlineColDef } from '../util/InlineTable';

// ── Тестовый вариант экрана ролей по «Дизайну №2» (master-detail) ──────────────
// Слева — вертикальный список ролей (влезает любое количество, без горизонтального
// скролла), справа — все разделы и права выбранной роли. Данные и логика сохранения
// переиспользуются из RoleTableLegacy. Бэкенд не меняется — тот же PUT /roles/:id.

interface Props {
  access: Record<string, any>;
  refreshUserHeder?: () => void;
}

// ── Карточка одного раздела для выбранной роли ─────────────────────────────────
function SectionCard({ group, role, onToggle, onToggleMany, canEdit }: {
  group: typeof GROUPS[0];
  role: Role;
  onToggle: (id: number, key: keyof Role) => void;
  onToggleMany: (id: number, keys: (keyof Role)[], value: boolean) => void;
  canEdit: boolean;
}) {
  const [open, setOpen] = useState(false);
  const masterKey = group.keys.find(k => k.isMaster)?.key;
  const subKeys = group.keys.filter(k => !k.isMaster);
  const isMasterOn = masterKey ? toBool(role[masterKey]) : true;
  const cnt = subKeys.reduce((c, k) => (toBool(role[k.key]) ? c + 1 : c), 0);
  const bd = '1px solid var(--border-new)';

  const sectionMasters = Object.fromEntries(
    group.keys.filter(k => k.isSectionMaster).map(k => [k.section, k])
  );

  const getSectionSubKeys = (section: string): (keyof Role)[] =>
    group.keys.filter(k => !k.isMaster && !k.isSectionMaster && k.section === section).map(k => k.key as keyof Role);

  return (
    <div style={{ border: bd, borderRadius: 10, background: 'var(--bg-form)', marginBottom: 8, overflow: 'hidden' }}>
      {/* Header */}
      <div
        onClick={() => subKeys.length > 0 && setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
          cursor: subKeys.length > 0 ? 'pointer' : 'default', background: open ? 'var(--hover-new)' : 'transparent',
        }}
      >
        <span style={{ fontSize: 16, flexShrink: 0 }}>{group.emoji}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-new)' }}>{group.name}</span>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          {subKeys.length > 0 && (
            <span style={{ fontSize: 11, color: 'var(--text2-new)', width: 40, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
              {cnt}/{subKeys.length}
            </span>
          )}
          {masterKey && (
            <span onClick={e => e.stopPropagation()}>
              <Toggle checked={isMasterOn} onChange={() => onToggle(role.id, masterKey)} disabled={!canEdit} />
            </span>
          )}
          {subKeys.length > 0 && (
            <span style={{ fontSize: 10, color: 'var(--text3-new)', width: 12, textAlign: 'center', display: 'inline-block', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>▶</span>
          )}
        </div>
      </div>

      {/* Expanded sub-permissions */}
      {open && (() => {
        let lastSection = '';
        return (
          <div style={{ borderTop: bd, padding: '4px 12px 8px', background: 'var(--surface2)' }}>
            {group.keys.filter(k => !k.isMaster && !k.isSectionMaster).map(child => {
              const sectionHeader = child.section && child.section !== lastSection;
              if (sectionHeader) lastSection = child.section!;
              const smDef = child.section ? sectionMasters[child.section] : undefined;
              const sectionMasterKey = smDef?.key;
              const roleSectionOn = sectionMasterKey ? toBool(role[sectionMasterKey]) : true;
              const disabled = !isMasterOn || !roleSectionOn;

              return (
                <React.Fragment key={String(child.key)}>
                  {sectionHeader && smDef && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0 5px', borderBottom: bd, opacity: isMasterOn ? 1 : 0.35 }}>
                      <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text2-new)', flex: 1 }}>
                        {child.section}
                      </span>
                      {canEdit && isMasterOn && (() => {
                        const subKs = getSectionSubKeys(child.section!);
                        if (!subKs.length) return null;
                        const allOn = subKs.every(k => toBool(role[k]));
                        return (
                          <button
                            type="button"
                            onClick={e => { e.stopPropagation(); onToggleMany(role.id, subKs, !allOn); }}
                            style={{ fontSize: 10.5, fontWeight: 600, color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', padding: '1px 7px', borderRadius: 4, whiteSpace: 'nowrap' }}
                          >
                            {allOn ? 'Clear all' : 'Select all'}
                          </button>
                        );
                      })()}
                      <Check
                        checked={toBool(role[sectionMasterKey!])}
                        onChange={() => isMasterOn && onToggle(role.id, sectionMasterKey as keyof Role)}
                        disabled={!isMasterOn || !canEdit}
                      />
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', paddingLeft: child.section ? 14 : 0, opacity: disabled ? 0.35 : 1 }}>
                    <span style={{ fontSize: 12.5, color: 'var(--text2-new)', flex: 1 }}>{child.label}</span>
                    <Check
                      checked={toBool(role[child.key])}
                      onChange={() => !disabled && canEdit && onToggle(role.id, child.key)}
                      disabled={disabled || !canEdit}
                    />
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}

// ── Основной компонент ─────────────────────────────────────────────────────────
const RoleMasterDetail: React.FC<Props> = ({ access, refreshUserHeder }) => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const canEdit = access?.settings_view === true || access?.settings_view === 1;

  const fetchRoles = useCallback(async () => {
    try {
      const { data } = await axios.get('roles');
      setRoles(data);
      setSelectedId(prev => (prev && data.some((r: Role) => r.id === prev)) ? prev : (data[0]?.id ?? null));
    } catch {
      // auth-ошибки обрабатывает глобальный interceptor
    }
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  const toStored = (b: boolean) => (b ? 1 : 0);

  const onToggleMany = async (roleId: number, keys: (keyof Role)[], value: boolean) => {
    const cur = roles.find(r => r.id === roleId);
    if (!cur) return;
    const storeVal = toStored(value);
    const updated = { ...cur, ...Object.fromEntries(keys.map(k => [k, storeVal])) } as Role;
    setRoles(prev => prev.map(r => r.id === roleId ? updated : r));
    try {
      await axios.put(`/roles/${roleId}`, Object.fromEntries(keys.map(k => [k, value])));
      refreshUserHeder?.();
    } catch {
      setRoles(prev => prev.map(r => r.id === roleId ? cur : r));
    }
  };

  const onToggle = async (roleId: number, key: keyof Role) => {
    const cur = roles.find(r => r.id === roleId);
    if (!cur) return;
    const newBool = !toBool(cur[key]);
    const updated = { ...cur, [key]: toStored(newBool) } as Role;
    setRoles(prev => prev.map(r => r.id === roleId ? updated : r));
    try {
      await axios.put(`/roles/${roleId}`, { [key]: newBool });
      refreshUserHeder?.();
    } catch {
      setRoles(prev => prev.map(r => r.id === roleId ? cur : r));
    }
  };

  const sorted = [...roles].sort((a, b) => a.id - b.id);
  const selected = sorted.find(r => r.id === selectedId) || null;
  const totalSections = GROUPS.length;
  const enabledSections = (role: Role) =>
    GROUPS.filter(g => { const mk = g.keys.find(k => k.isMaster)?.key; return mk ? toBool(role[mk]) : false; }).length;

  // ── CRUD ролей через общий InlineTable ──────────────────────────────────────
  const onAddRole = async (form: Partial<Role>) => {
    const title = (form.title ?? '').trim();
    if (!title) return;
    await axios.post('roles/add', { title });
    const { data } = await axios.get('roles');
    const created = [...data].sort((a: Role, b: Role) => b.id - a.id)[0];
    if (created && form.color) await axios.put(`/roles/${created.id}`, { color: form.color });
    await fetchRoles();
    if (created) setSelectedId(created.id);
    refreshUserHeder?.();
  };

  const onEditRole = async (id: string | number, form: Partial<Role>) => {
    const payload: any = {};
    if (form.title != null) payload.title = String(form.title).trim();
    if (form.color != null) payload.color = form.color;
    await axios.put(`/roles/${id}`, payload);
    await fetchRoles();
    refreshUserHeder?.();
  };

  const onDeleteRole = async (id: string | number) => {
    await axios.delete(`/roles/${id}`);
    if (selectedId === id) setSelectedId(null);
    await fetchRoles();
  };

  const onSetDefault = async (id: number) => {
    setRoles(prev => prev.map(r => ({ ...r, isDefault: r.id === id })));
    try {
      await axios.put(`/roles/${id}/set-default`);
      await fetchRoles();
    } catch { await fetchRoles(); }
  };

  // Системные роли (admin/user) нельзя удалять — маркируются через description (PrimaryRolesId)
  const isSystemRole = (r: Role) => r.description === -1 || r.description === 0;

  const roleColumns: InlineColDef<Role>[] = [
    { key: 'color', label: '', type: 'color', width: 46 },
    {
      key: 'title', label: t('Roles') as string, type: 'text',
      placeholder: t('EnterNameOfRole') as string,
      render: (r) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
          <span onClick={() => setSelectedId(r.id)}
            style={{ flex: 1, cursor: 'pointer', fontWeight: r.id === selectedId ? 700 : 500, color: r.id === selectedId ? 'var(--text-color)' : 'var(--text-color-calendar)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {r.title}
          </span>
          {isSystemRole(r) && (
            <span title={t('SystemRoleHint') as string}
              style={{ flexShrink: 0, fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-color-calendar-week)', border: '1px solid var(--border-input)', borderRadius: 4, padding: '1px 5px', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              🔒 {t('SystemRole') || 'System'}
            </span>
          )}
          {canEdit && (
            <button type="button"
              title={(r.isDefault ? t('DefaultRoleForNew') : t('MakeDefaultForNew')) as string}
              onClick={(e) => { e.stopPropagation(); if (!r.isDefault) onSetDefault(r.id); }}
              className="role-default-star"
              style={{ flexShrink: 0, background: 'none', border: 'none', cursor: r.isDefault ? 'default' : 'pointer', fontSize: 14, lineHeight: 1, padding: 2, color: r.isDefault ? '#f59e0b' : 'var(--text-color-calendar-week)', opacity: r.isDefault ? 1 : undefined }}>
              {r.isDefault ? '★' : '☆'}
            </button>
          )}
        </span>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', gap: 12, height: '100%', minHeight: 0 }}>
      {/* Left — roles (общий util/InlineTable) */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', border: 'var(--border-input)', borderRadius: 'var(--radius-card, 10px)', background: 'var(--bg-form)', overflow: 'hidden' }}>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <InlineTable<Role>
            columns={roleColumns}
            rows={sorted}
            getKey={(r) => r.id}
            onAdd={canEdit ? onAddRole : undefined}
            onEdit={canEdit ? onEditRole : undefined}
            onDelete={canEdit ? onDeleteRole : undefined}
            canDelete={(r) => !r.isDefault && !isSystemRole(r)}
            defaultValues={{ color: '#2563eb' } as Partial<Role>}
            addLabel={t('CreateRole') as string}
            emptyText={t('Roles') as string}
          />
        </div>
      </div>

      {/* Right — selected role permissions */}
      <div style={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
        {selected ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{ width: 12, height: 12, borderRadius: '50%', background: selected.color || 'var(--text3-new)' }} />
              <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-new)' }}>{selected.title}</span>
              <span style={{ fontSize: 12, color: 'var(--text2-new)' }}>· {enabledSections(selected)}/{totalSections} sections</span>
            </div>
            {GROUPS.map(group => (
              <SectionCard key={group.name} group={group} role={selected} onToggle={onToggle} onToggleMany={onToggleMany} canEdit={canEdit} />
            ))}
          </>
        ) : (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3-new)', fontSize: 13 }}>
            Select a role
          </div>
        )}
      </div>
    </div>
  );
};

export default RoleMasterDetail;
